"use client";
import React, { useRef, useState } from "react";

import { cn } from "../lib/utils";
import { TextAreaInput } from "./TextAreaInput";

interface AITextAreaInputProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  containerClassName?: string;
  errortext?: string;
  helpertext?: string;
  /** Fetch a suggestion for the current value. API-agnostic: the consumer
   * decides what backend to call. A rejected promise surfaces as a brief
   * inline error. */
  onSuggest: (currentValue: string) => Promise<string>;
  /** Apply an accepted suggestion (e.g. formik setFieldValue). Called with the
   * suggestion only when the field was not edited while the request was in
   * flight. */
  onSuggestion: (suggestion: string) => void;
  /** Accessible label for the suggest button. */
  suggestLabel?: string;
}

export function AITextAreaInput(props: AITextAreaInputProps) {
  const {
    label,
    containerClassName,
    errortext,
    helpertext,
    onSuggest,
    onSuggestion,
    suggestLabel = "Suggest description",
    className,
    value,
    ...textareaProps
  } = props;
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | undefined>(
    undefined,
  );

  const currentValue = typeof value === "string" ? value : "";
  // Tracks the latest rendered value so an in-flight suggestion is dropped
  // instead of overwriting text the user typed while waiting.
  const latestValueRef = useRef(currentValue);
  latestValueRef.current = currentValue;

  const handleSuggest = async () => {
    if (suggesting) {
      return;
    }
    setSuggesting(true);
    setSuggestError(undefined);
    const requestValue = currentValue;
    try {
      const suggestion = await onSuggest(requestValue);
      if (latestValueRef.current === requestValue) {
        onSuggestion(suggestion);
      }
    } catch {
      setSuggestError("Couldn't generate a suggestion. Try again.");
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className={containerClassName}>
      {label && (
        <label
          htmlFor={props.id}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <TextAreaInput
          {...textareaProps}
          value={value}
          className={cn("pr-9", className)}
          errortext={errortext ?? suggestError}
          helpertext={helpertext}
        />
        <button
          type="button"
          aria-label={suggestLabel}
          title={suggestLabel}
          disabled={suggesting}
          onClick={() => void handleSuggest()}
          className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:text-blue-500 disabled:cursor-default disabled:hover:text-gray-400"
        >
          {suggesting ? (
            <span
              aria-hidden="true"
              className="block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500"
            />
          ) : (
            <span className="iconoir-sparks text-lg" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}
